import clsx from 'clsx'
import { type ReactNode, useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'

// Same focusable-element selector + helper used by Modal — kept in sync
// because both components implement the WAI-ARIA dialog focus-trap pattern.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',')

function getFocusable(root: HTMLElement): HTMLElement[] {
  const nodes = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
  return nodes.filter((n) => !n.hasAttribute('aria-hidden') && n.offsetParent !== null)
}

export type DrawerSide = 'left' | 'right'

export interface DrawerProps {
  open: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  side?: DrawerSide
  width?: number
  closeOnBackdrop?: boolean
}

export function Drawer({
  open,
  onClose,
  title,
  children,
  side = 'right',
  width = 360,
  closeOnBackdrop = true,
}: DrawerProps) {
  const labelId = useId()
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      // Focus trap — same logic as Modal. Without this Tab eventually walks
      // out of the drawer into the (visually hidden but still focusable)
      // page behind it.
      if (e.key !== 'Tab') return
      const drawer = ref.current
      if (!drawer) return
      const focusable = getFocusable(drawer)
      if (focusable.length === 0) {
        e.preventDefault()
        drawer.focus()
        return
      }
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement as HTMLElement | null
      if (e.shiftKey) {
        if (active === first || !drawer.contains(active)) {
          e.preventDefault()
          last?.focus()
        }
      } else {
        if (active === last || !drawer.contains(active)) {
          e.preventDefault()
          first?.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const previouslyFocused = document.activeElement as HTMLElement | null
    ref.current?.focus()
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
      previouslyFocused?.focus?.()
    }
  }, [open, onClose])

  if (!open) return null
  if (typeof document === 'undefined') return null

  return createPortal(
    <div className="fixed inset-0 z-50" role="presentation">
      <button
        type="button"
        aria-label="Close drawer"
        tabIndex={-1}
        onClick={() => closeOnBackdrop && onClose()}
        className="absolute inset-0 bg-black/50 cursor-default"
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? labelId : undefined}
        tabIndex={-1}
        style={{ width }}
        className={clsx(
          'absolute top-0 bottom-0 flex flex-col bg-[var(--bg-2)] border-[var(--border)] outline-none',
          side === 'left' ? 'left-0 border-r' : 'right-0 border-l',
        )}
      >
        {title ? (
          <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-3">
            <h2 id={labelId} className="text-[15px] font-semibold text-[var(--text)]">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="text-[var(--text-2)] hover:text-[var(--text)] outline-none focus-visible:shadow-[var(--focus-ring)] rounded-[var(--radius-sm)] px-1"
            >
              ×
            </button>
          </div>
        ) : null}
        <div className="flex-1 overflow-auto p-4">{children}</div>
      </div>
    </div>,
    document.body,
  )
}
